import type { Locale, SpecificationGroup, ToppingTemplate } from "@tovi/shared";
import type { LineOption } from "./cart.ts";
import { displayName } from "./menu.ts";

/**
 * What the item sheet has to ask, and what the answers cost.
 *
 * `GET /store/items` names an item's modifiers by id and nothing else, so the
 * till fetches the two catalogues once and resolves them here. Both are
 * normalised into one shape: a specification group and a topping template
 * differ in what they mean, not in what a cashier does with them, and one
 * shape is what lets the sheet render either with the same code.
 *
 * Pure, and out of the component on purpose. Which options open selected,
 * which group still owes an answer and what a second tap on a chosen size
 * does are the rules the money depends on, and they are the part a test can
 * reach without a renderer.
 */

/** One answer, priced per unit. */
export type Choice = {
  readonly id: string;
  readonly name: string;
  readonly price: bigint;
  /** The merchant's own default. It opens selected. */
  readonly isDefault: boolean;
};

export type ChoiceGroup = {
  readonly id: string;
  readonly name: string;
  /** An answer is owed before the line can be added. Toppings never owe one. */
  readonly isRequired: boolean;
  /** One answer replaces the last. Otherwise every tap adds another. */
  readonly isSingle: boolean;
  readonly options: readonly Choice[];
};

/**
 * Kept apart rather than concatenated: they price identically and travel to
 * the server in two different fields (`OrderLineInput`).
 */
export type Choices = {
  readonly specifications: readonly ChoiceGroup[];
  readonly toppings: readonly ChoiceGroup[];
};

/** Which option ids are chosen, by group id. */
export type Chosen = Readonly<Record<string, readonly string[]>>;

/** An item that asks nothing, and what an unloaded catalogue answers with. */
export const NO_CHOICES: Choices = { specifications: [], toppings: [] };

/** What an item links, which is what it will ask once the catalogues are here. */
export type Modified = {
  readonly specificationGroupIds: readonly string[];
  readonly toppingTemplateIds: readonly string[];
};

/**
 * Whether a tap has to become a sheet.
 *
 * One tap cannot answer "which size?", so an item that asks it is routed to
 * the sheet instead of onto the bill. The alternative — adding at base price
 * and trusting somebody to fix it later — is a line the station cannot cook
 * and a total the customer is holding.
 *
 * Read off the item's own links rather than off the resolved groups: the two
 * disagree exactly while the catalogues are loading, and an item that asks
 * something has to wait for its question rather than skip it.
 *
 * Tags are not part of it. A tag is an unpriced station instruction, so it
 * changes no bill and need not stop a one-tap add.
 */
export function asksSomething(item: Modified): boolean {
  return item.specificationGroupIds.length > 0 || item.toppingTemplateIds.length > 0;
}

/**
 * The item's modifiers, in the order the merchant linked them.
 *
 * Driven by the item's own id lists rather than by the catalogues, so the
 * order a cashier reads is the order the item was configured in. An id that
 * resolves to nothing is skipped in silence: it is a group the plan no longer
 * sells or one this terminal has not fetched, and neither is a reason to
 * refuse the sale.
 *
 * A disabled group is dropped for the same reason the diner drops it — the
 * merchant turned it off, and an off group must not put money on a bill. So is
 * a disabled option under an enabled group: one size taken off the menu is the
 * same instruction as the whole group, said about less of it.
 *
 * That filter lives here rather than in the endpoint the catalogues come from,
 * because `/store/specification-groups` is also what the back office reads to
 * edit them — a merchant has to see a hidden option to un-hide it, and the save
 * is a full replace, so an option the editor could not read back is an option
 * the next save deletes. Filtering at the till instead means the download
 * carries `status` and the cached copy decides the same way the live one does,
 * which is the only version of this that survives going offline.
 *
 * A required group whose every option is hidden keeps its place and offers
 * nothing: `unanswered` then names it on the Add button, and the item cannot be
 * sold. Dropping the group instead would add the item at base price with no
 * size — a bill and a ticket that are both quietly wrong, which is worse than a
 * cashier who can see why the sale will not go through.
 *
 * Every name goes through `displayName`, the same helper the tile and the
 * search use: a group and an option both satisfy `Named` now that the contract
 * carries their `i18n`. It has to happen here rather than in the sheet because
 * the name a cashier chose is the name `chosenIn` puts on the line — and from
 * there onto the ticket and the receipt, where it can no longer be changed.
 */
export function choicesFor(
  item: Modified,
  groups: readonly SpecificationGroup[],
  templates: readonly ToppingTemplate[],
  locale: Locale,
): Choices {
  return {
    specifications: item.specificationGroupIds.flatMap((id) => {
      const group = groups.find((row) => row.id === id);
      if (!group || group.status !== "enabled") return [];
      return [
        {
          id: group.id,
          name: displayName(group, locale),
          isRequired: group.isRequired,
          isSingle: group.selection === "single",
          options: group.options
            .filter((option) => option.status === "enabled")
            .map((option) => ({
              id: option.id,
              name: displayName(option, locale),
              price: BigInt(option.priceDelta),
              isDefault: option.isDefault,
            })),
        },
      ];
    }),
    toppings: item.toppingTemplateIds.flatMap((id) => {
      const template = templates.find((row) => row.id === id);
      if (!template || template.status !== "enabled") return [];
      return [
        {
          id: template.id,
          name: displayName(template, locale),
          // An extra is an addition, never an answer that is owed: a cashier
          // must be able to send a pizza with nothing on it.
          isRequired: false,
          isSingle: template.selection === "single",
          options: template.items
            .filter((topping) => topping.status === "enabled")
            .map((topping) => ({
              id: topping.id,
              name: displayName(topping, locale),
              price: BigInt(topping.price),
              isDefault: topping.defaultSelect,
            })),
        },
      ];
    }),
  };
}

/**
 * The sheet as it opens.
 *
 * A default is the merchant saying what this normally is, so honouring it
 * turns the common order back into one tap on Add — which is the whole reason
 * the sheet is allowed to interrupt a tap at all. Nothing is hidden by it: the
 * selection and the total it makes are on screen before anything is added.
 *
 * One default per single-choice group, whatever the catalogue holds: two
 * marked in a group that takes one answer is a configuration mistake, and the
 * first is a better answer to it than an illegal selection.
 */
export function defaultsFor(choices: Choices): Chosen {
  const chosen: Record<string, readonly string[]> = {};
  for (const group of [...choices.specifications, ...choices.toppings]) {
    const defaults = group.options.filter((option) => option.isDefault).map((option) => option.id);
    if (defaults.length === 0) continue;
    chosen[group.id] = group.isSingle ? defaults.slice(0, 1) : defaults;
  }
  return chosen;
}

/** What a tap on an option does. */
export function pick(chosen: Chosen, group: ChoiceGroup, optionId: string): Chosen {
  const held = chosen[group.id] ?? [];
  if (!held.includes(optionId)) {
    return { ...chosen, [group.id]: group.isSingle ? [optionId] : [...held, optionId] };
  }
  // A required single-choice group behaves like a radio: the answer changes,
  // it does not go away. A second tap on the chosen size is a cashier aiming
  // at the size they already have, not asking for no size at all — and the
  // group would owe an answer again the moment it cleared.
  if (group.isSingle && group.isRequired) return chosen;
  return { ...chosen, [group.id]: held.filter((id) => id !== optionId) };
}

/** The chosen options of these groups, in the order they were asked. */
export function chosenIn(groups: readonly ChoiceGroup[], chosen: Chosen): readonly LineOption[] {
  return groups.flatMap((group) =>
    group.options
      .filter((option) => (chosen[group.id] ?? []).includes(option.id))
      .map((option) => ({ name: option.name, price: option.price })),
  );
}

/**
 * The groups still owing an answer.
 *
 * Named rather than counted, because the button says which one is missing:
 * a disabled Add with no reason on it is a cashier holding up a queue while
 * they work out what the sheet wants.
 */
export function unanswered(choices: Choices, chosen: Chosen): readonly ChoiceGroup[] {
  return [...choices.specifications, ...choices.toppings].filter(
    (group) => group.isRequired && (chosen[group.id]?.length ?? 0) === 0,
  );
}
